import { useEffect, useState, useContext, useMemo, useRef } from "react";
import L from "leaflet";
import { useDispatch, useSelector } from "react-redux";
import {
  ImageOverlay,
  MapContainer,
  Marker,
  Popup,
  Tooltip,
  useMap
} from "react-leaflet";
import {
  setProdLinesData,
  setBattStationsData,
  setDockDoorsData,
  setIsNonLgvModalOpen,
  setPlantMetaData,
  setVehicleDataFromSocket,
  setFollowingLgvList,
  setRacksCount,
  setRackStorageData,
  setBlockStorageData,
  setBlocksCount,
  setSelectedBlockRackLocationData,
  setEnableDockDoor,
  setDockDoorViewIsOpen,
  setEnableProductionLine,
  setProductionLineViewIsOpen
} from "../../store/plantSlice";
import {
  setBlocksData,
  setLGVsData,
  setRacksData
} from "../../store/inventorySlice";
import {
  combineLGVDetailWithCoordinateData,
  convertReactComponentToHtmlIcon,
  lgvNameRenderer,
  mapCoordinate,
  setDynamicZoomLevelToMap,
  isBlockRackNonLgvEnabled,
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDragLeave,
  formatName,
  getLocationGroupOffset,
  getDestinationPointerOffset,
  setBlockStorageSize,
  setVerticalBlockStorageSize,
  getPlantMapImageUrl,
  countBlocksUsingLPNs,
  countRacksUsingLPNs,
  setRackStorageSize,
  rotateMarker,
  getDestinationPointerOffsetV2,
  statusColorRenderer,
  GenerateHTMLBlockIcon,
  getConvertedLatLng,
  usesNewPointerLogic,
  getDockPosition
} from "../../util/helper/helperFunctions";
import {
  DB_ERROR,
  SOCKET_CHANNELS,
  BLOCK_RACK_POLLING_INTERVAL,
  indoorMapZoomScale,
  maxZoomLgvIconSize
} from "../../util/helper/constants";
import {
  deTransformLGVName,
  normalizeData,
  normalizeLGVData
} from "../FilterDrawer/utils";

import { AppContext } from "../../Context/Context";

import LegendsButton from "./LegendsButton";
import DockIcon from "../DockDoor/DockIcon";
import LgvIcon from "../LgvIcon/LgvIcon";
import ProdLineCard from "./ProdLineCard";
import BatteryStationObject from "../BatteryStation/BatteryStationObject";
import CustomPopup from "./CustomPopup";
import BlockMarker from "./BlockMarker";
import BillboardBanner from "../BillboardBanner/BillboardBanner";
import ZoomControls from "./ZoomControls";
import TrailerArrivalNotification from "../TrailerArrivalNotification/TrailerArrivalNotification";
import ReValidateMap from "./ReValidateMap";
import FilterButton from "./FilterButton";

import {
  prodLineCoordinateConverter,
  battStationCoordinateConverter,
  dockDoorCoordinateConverter,
  nonLGVCoordinateConverter,
  blockStorageCoordinateConverter,
  rackStorageCoordinateConverter
} from "./coordConverter";

import "leaflet-rotatedmarker";
import "leaflet/dist/leaflet.css";
import "../../assets/css/Map/map.css";
import PanToLocation from "./PanTo";
import { blockStorage, rackStorage } from "../../util/network/apiUrl";
import { fetchDataWithRetry } from "../../util/network/databricksHandler";
import DestinationPointer from "./DestinationPointers";
import LgvMarker from "./LgvMarker";
import { useSocketDataEffect } from "../../hooks/useSocketData";
import { useParams } from "react-router-dom";
import MinimizedWebObjects from "../MinimizedObject/MinimizedWebObjects";
import FollowingLgvObjects from "../MinimizedObject/FollowingLgvObjects";
import MaxZoomLGVIcon from "../LgvIcon/MaxZoomLGVIcon";

const Loader = ({ connectionStatus, label = "Map loading in-progress..." }) => (
  <div className="loader">
    <h4>
      {connectionStatus ? label : "Socket initialiasation in-progress..."}
    </h4>
    <div className="spinner"></div>
  </div>
);

const IndoorMap = ({ socket }) => {
  const { plantCode } = useParams();
  const dispatch = useDispatch();
  const mapRef = useRef(null);
  const markerRefs = useRef({});
  const prevHoveredRef = useRef(null);
  const latestLGVData = useRef([]);
  const lgvCoordProcessing = useRef(false);
  const [mapReady, setMapReady] = useState(false);

  const {
    connectionStatus,
    appConfig,
    enableDestinationPointers,
    isMapDragEnable,
    openedBatteryStationName,
    plantConfig,
    setSelectedMarkerType,
    isBlockRackModalOpen,
    setIsBlockRackModalOpen,
    setIsMarkerPopupOpen,
    setSelectedMarkerData,
    auth,
    setIsRackModal,
    openedLgvVehichleNumber
  } = useContext(AppContext);

  const enableProductionLine = useSelector(
    (state) => state.plant.enableProductionLine
  );

  const productionLineViewIsOpen = useSelector(
    (state) => state.plant.productionLineViewIsOpen
  );

  const enableBatteryStationObj = useSelector(
    (state) => state.plant.enableBatteryStationObj
  );

  const lgvObjectViewIsOpen = useSelector(
    (state) => state.plant.lgvObjectViewIsOpen
  );
  const { data: vehicleDataFromSocket } = useSelector(
    (state) => state.plant.vehicleDataFromSocket
  );
  const followingLgvList = useSelector((state) => state.plant.followingLgvList);
  const racksCount = useSelector((state) => state.plant.rackStorage.count);
  const blocksCount = useSelector((state) => state.plant.blockStorage.count);
  const { data: rackStorageData } = useSelector(
    (state) => state.plant.rackStorage
  );
  const { data: blockStorageData } = useSelector(
    (state) => state.plant.blockStorage
  );

  const dockDoorViewIsOpen = useSelector(
    (state) => state.plant.dockDoorViewIsOpen
  );
  const enableDockDoor = useSelector((state) => state.plant.enableDockDoor);
  const nonLgvData = useSelector((state) => state.plant.nonLGVInventory.data);
  const [mapLoader, setMapLoader] = useState(true);
  const [convertedSocketData, setConvertedSocketData] = useState([]);
  const [mapObject, setMapObject] = useState();
  const [zoomLevel, setZoomLevel] = useState(0);
  const [currentWheelZoom, setCurrentWheelZoom] = useState(1);

  const [popUpDetails, setPopUpDetails] = useState(null);
  const [hoveredLocation, setHoveredLocation] = useState(null);

  useEffect(() => {
    const prevLocation = prevHoveredRef.current;
    if (prevLocation && prevLocation !== hoveredLocation) {
      const prevIcon = markerRefs.current[prevLocation]?._icon;
      if (prevIcon) {
        prevIcon.classList.remove("outline-yellow");
      }
    }

    const newIcon = markerRefs.current[hoveredLocation]?._icon;
    if (newIcon && !isBlockRackModalOpen) {
      newIcon.classList.add("outline-yellow");
    }

    prevHoveredRef.current = hoveredLocation;
  }, [hoveredLocation, isBlockRackModalOpen]);

  const {
    results: filterResults,
    items: { lgvs },
    selectedResult
  } = useSelector((state) => state.inventory);

  //Customize dashboard switch settings
  useEffect(() => {
    if (localStorage.getItem("appSettings")) {
      let prevSwitchState = JSON.parse(localStorage.getItem("appSettings"));
      if (prevSwitchState.productionLines === true) {
        dispatch(setProductionLineViewIsOpen(true));
      }
      if (prevSwitchState.dockDoors === true) {
        dispatch(setDockDoorViewIsOpen(true));
      }
    }
  }, []);

  useEffect(() => {
    mapRef.current?.dragging[isMapDragEnable ? "enable" : "disable"]();
  }, [isMapDragEnable]);

  //Format Raw LGV data to Marker data set
  const lgvJsonFormatter = (data) => {
    let jsonArray = [];
    if (data && data !== DB_ERROR) {
      let filteredData = data?.filter((item) => {
        return item.longitude !== null && item.latitude != null;
      });
      if (filteredData.length !== 0) {
        filteredData.forEach((dataValue) => {
          let formattedJson = {
            destination: "",
            source: "",
            duration: "",
            lgvName: "",
            lpn: "",
            segment: "",
            sku: "",
            status: "",
            vehicleNumber: "",
            currentPos: {
              lat: "",
              lng: ""
            },
            path: []
          };

          Object.entries(dataValue).forEach(([key, value]) => {
            formattedJson[key] = dataValue[key];
          });
          formattedJson.currentPos["lat"] = dataValue.latitude;
          formattedJson.currentPos["lng"] = dataValue.longitude;
          jsonArray.push(formattedJson);
        });
        return jsonArray;
      } else {
        return [];
      }
    }
  };

  /* dispatches only if there is any new / removed LGV by comparing incoming and existing lgvs*/
  const compareLgvData = (newData) => {
    if (lgvs.length) {
      const currentLgvist = new Set(
        lgvs.map((item) => deTransformLGVName(item.location))
      );
      const newLgvList = new Set(newData.map((item) => item.vehicleNumber));

      const hasChanged =
        newLgvList.size !== currentLgvist.size ||
        [...newLgvList].some((item) => !currentLgvist.has(item));

      hasChanged &&
        dispatch(setLGVsData(normalizeLGVData(vehicleDataFromSocket)));
    } else if (lgvs.length === 0) {
      dispatch(setLGVsData(normalizeLGVData(vehicleDataFromSocket)));
    }
  };

  //Convert LGV Coordinate from Ignition system to LGV One system
  const lgvCoordinateConverter = () => {
    try {
      if (appConfig && plantConfig && mapObject) {
        const {
          indoorMap: {
            lgvs: { bounds }
          }
        } = plantConfig;

        const updatedVehicleData = vehicleDataFromSocket.map((marker) => ({
          ...marker,
          currentPos: getConvertedLatLng(
            marker.longitude,
            marker.latitude,
            bounds,
            mapObject
          ),
          destinationCurrentPos: getConvertedLatLng(
            marker.destinationLongitude,
            marker.destinationLatitude,
            bounds,
            mapObject
          ),
          sourceCurrentPos: getConvertedLatLng(
            marker.sourceLongitude,
            marker.sourceLatitude,
            bounds,
            mapObject
          )
        }));
        setConvertedSocketData(updatedVehicleData);
        compareLgvData(updatedVehicleData);
      }
    } catch (err) {
      console.error("Error in lgvCoordinateConverter:", err);
    }
  };

  const processDockDoorData = (data) => {
    const groupedByDockName = Object.groupBy(data, ({ dock }) => dock);

    const convertedDoorData = dockDoorCoordinateConverter(
      Object.values(groupedByDockName),
      plantConfig.indoorMap.dockDoors,
      mapObject
    );

    return convertedDoorData;
  };

  const { data: convertedProdLineData } = useSelector(
    (state) => state.plant.prodLines
  );
  const { data: convertedBattStationData } = useSelector(
    (state) => state.plant.battStations
  );
  const { data: convertedDockDoorsData } = useSelector(
    (state) => state.plant.dockDoors
  );

  const {
    productionLinesChannelDetails,
    batteryStationsChannelDetails,
    dockDoorChannelDetails
  } = useMemo(
    () => ({
      productionLinesChannelDetails: {
        channel: SOCKET_CHANNELS.PRODUCTION_LINES,
        dataHandler: (data) => {
          if (plantConfig && mapObject) {
            const prodLines = prodLineCoordinateConverter(
              data,
              plantConfig.indoorMap.productionLines,
              mapObject
            );
            dispatch(setProdLinesData(prodLines));
          }
        }
      },
      batteryStationsChannelDetails: {
        channel: SOCKET_CHANNELS.BATTERY_STATIONS,
        dataHandler: (data) => {
          if (plantConfig && mapObject) {
            const battStations = battStationCoordinateConverter(
              data,
              plantConfig.indoorMap.batteryStations,
              mapObject
            );
            dispatch(setBattStationsData(battStations));
          }
        }
      },
      dockDoorChannelDetails: {
        channel: SOCKET_CHANNELS.DOCK_DOORS,
        dataHandler: (data) => {
          if (plantConfig && mapObject) {
            dispatch(setDockDoorsData(processDockDoorData(data)));
          }
        }
      }
    }),
    [mapObject, plantConfig]
  );

  useSocketDataEffect({
    socket,
    channel: productionLinesChannelDetails.channel,
    onData: productionLinesChannelDetails.dataHandler
  });

  useSocketDataEffect({
    socket,
    channel: batteryStationsChannelDetails.channel,
    onData: batteryStationsChannelDetails.dataHandler
  });

  useSocketDataEffect({
    socket,
    channel: dockDoorChannelDetails.channel,
    onData: dockDoorChannelDetails.dataHandler
  });

  //Move Map to the center of view
  const centerMapView = (unit1, unit2) => {
    let convertedCoordinates;
    if (unit1 && unit2) {
      convertedCoordinates = mapCoordinate(unit1, unit2);
    } else {
      convertedCoordinates = mapCoordinate(275000, 70000);
    }
    let convertedPoints = L.point(
      convertedCoordinates.x,
      convertedCoordinates.y
    );
    let convertedLatLng =
      mapObject?.map.target.layerPointToLatLng(convertedPoints);
    mapObject?.map.target.setView([convertedLatLng.lat, convertedLatLng.lng]);
  };

  //LGV logic channel data

  const shouldSubscribe = plantConfig && plantCode;

  //data handlers for different channels
  const lgvDetailsHandler = (data) => {
    if (data.length && data !== DB_ERROR) {
      latestLGVData.current = data;
    }
  };

  const kafkaHandler = (kafkaData) => {
    if (kafkaData.length && kafkaData !== DB_ERROR) {
      const filtered = kafkaData.filter(
        (lgv) => lgv.latitude !== "undefined" && lgv.longitude !== "undefined"
      );
      const combined = combineLGVDetailWithCoordinateData(
        filtered,
        latestLGVData.current
      );
      dispatch(setVehicleDataFromSocket(lgvJsonFormatter(combined)));
    }
  };

  const coordinateHandler = (coordData) => {
    if (
      coordData.length &&
      coordData !== DB_ERROR &&
      latestLGVData.current.length &&
      !lgvCoordProcessing.current
    ) {
      lgvCoordProcessing.current = true;
      setTimeout(() => {
        lgvCoordProcessing.current = false;
      }, 1000);

      const filtered = coordData.filter(
        (lgv) => lgv.latitude !== "undefined" && lgv.longitude !== "undefined"
      );
      const combined = combineLGVDetailWithCoordinateData(
        filtered,
        latestLGVData.current
      );
      dispatch(setVehicleDataFromSocket(lgvJsonFormatter(combined)));
    }
  };

  const configHandler = (response) => {
    if (Array.isArray(response) && response.length) {
      dispatch(setPlantMetaData(response));
    }
  };

  const defaulfHookParam = {
    socket: null,
    channel: null,
    onData: () => {}
  };

  useSocketDataEffect(
    shouldSubscribe
      ? {
          socket,
          channel: SOCKET_CHANNELS.LGV_DETAILS,
          onData: lgvDetailsHandler
        }
      : defaulfHookParam
  );

  useSocketDataEffect(
    shouldSubscribe
      ? {
          socket,
          channel: plantConfig?.isKafkaEnabled
            ? SOCKET_CHANNELS.LGV_POSITION
            : SOCKET_CHANNELS.LGV_COORDINATES,
          onData: plantConfig?.isKafkaEnabled ? kafkaHandler : coordinateHandler
        }
      : defaulfHookParam
  );

  //Convert LGV, dock Door and Production Line coordinates if data available
  useEffect(() => {
    if (vehicleDataFromSocket) {
      lgvCoordinateConverter();
    }
  }, [vehicleDataFromSocket]);

  //Adjust the Map to the screen
  useEffect(() => {
    if (mapObject) {
      let screenSize = window.innerWidth;
      if (screenSize >= 1440 && screenSize < 1800) {
        setTimeout(() => {
          centerMapView();
          setDynamicZoomLevelToMap("1.0", "indoor");
        }, 1000);
      } else if (screenSize >= 1800 && screenSize < 2500) {
        setTimeout(() => {
          centerMapView(275000, 75000);
          setDynamicZoomLevelToMap("1.0", "indoor");
        }, 500);
      } else if (screenSize >= 2560 && screenSize < 3000) {
        setTimeout(() => {
          centerMapView(280000, 75000);
          setDynamicZoomLevelToMap("1.0", "indoor");
        }, 500);
      } else if (screenSize >= 3000 && screenSize < 4000) {
        setTimeout(() => {
          centerMapView(312000, 60000);
          setDynamicZoomLevelToMap("1.4", "indoor");
        }, 500);
      } else if (screenSize >= 4000 && screenSize < 5000) {
        setTimeout(() => {
          centerMapView(350000, 40000);
          setDynamicZoomLevelToMap("1.4", "indoor");
        }, 500);
      } else if (screenSize >= 5000 && screenSize < 6000) {
        setTimeout(() => {
          centerMapView(312000, 60000);
          setDynamicZoomLevelToMap("2.0", "indoor");
        }, 500);
      } else {
        centerMapView();
      }
      mapObject.map.target.doubleClickZoom.disable();
    }
  }, [mapObject, window.innerWidth]);

  //Remove Dock door if disabled
  useEffect(() => {
    if (dockDoorViewIsOpen === false) {
      dispatch(setEnableDockDoor(false));
    }
  }, [dockDoorViewIsOpen]);

  //Remove Production Line if disabled
  useEffect(() => {
    if (productionLineViewIsOpen === false) {
      dispatch(setEnableProductionLine(false));
    }
  }, [productionLineViewIsOpen]);

  const maxZoom = indoorMapZoomScale.length - 1;
  //Render LGV Markers on the map
  const RenderLgv = useMemo(() => {
    if (plantConfig && convertedSocketData && lgvObjectViewIsOpen) {
      const {
        indoorMap: {
          lgvs: { marker, groupOffset, isEnabled }
        }
      } = plantConfig;
      if (!isEnabled) return null;

      return convertedSocketData.map((lgvMarker, index) => {
        const isLgvBeingFollowed = followingLgvList["indoorMap"].find(
          ({ vehicleNumber }) => vehicleNumber === lgvMarker.vehicleNumber
        );

        if (lgvMarker.vehicleNumber?.toString() == openedLgvVehichleNumber) {
          setSelectedMarkerData(lgvMarker);
        }

        const reactIcon = convertReactComponentToHtmlIcon(
          zoomLevel === maxZoom ? (
            <MaxZoomLGVIcon
              status={lgvMarker.status}
              alarmName={lgvMarker.alarmName}
              vehicleNumber={lgvMarker.vehicleNumber}
              angle={lgvMarker.angle}
              isFollowing={isLgvBeingFollowed}
            />
          ) : (
            <LgvIcon
              data={lgvNameRenderer(lgvMarker.vehicleNumber)}
              status={lgvMarker.status}
              alarmName={lgvMarker.alarmName}
              isFollowing={isLgvBeingFollowed}
              initialWidth={marker.size?.[0]}
            />
          ),
          "lgv-icon",
          zoomLevel === maxZoom ? maxZoomLgvIconSize : marker.size,
          zoomLevel === maxZoom ? [4.5, 4.5] : [40.5, 48]
        );

        return (
          <LgvMarker
            key={index}
            index={index}
            lgvMarker={lgvMarker}
            icon={reactIcon}
            groupOffset={groupOffset}
            isMaxZoom={zoomLevel === maxZoom}
            onClick={() => {
              setSelectedMarkerData(lgvMarker);
              setSelectedMarkerType("lgv");
              setIsMarkerPopupOpen(true);
            }}
          />
        );
      });
    }
  }, [
    convertedSocketData,
    followingLgvList,
    plantConfig,
    zoomLevel,
    maxZoom,
    lgvObjectViewIsOpen
  ]);

  //Filter module - LGV higlight
  useEffect(() => {
    let lgvToBeHighlighted = null;

    for (const item of filterResults) {
      if (item.storageType === "lgv") {
        lgvToBeHighlighted = deTransformLGVName(item.location);
        break;
      }
    }

    if (!lgvToBeHighlighted) {
      dispatch(setFollowingLgvList({ feature: "indoorMap", list: [] }));
      return;
    }

    const LGV = convertedSocketData?.filter(
      (item) => lgvToBeHighlighted === item.vehicleNumber
    );
    dispatch(setFollowingLgvList({ feature: "indoorMap", list: LGV }));
  }, [filterResults]);

  //Render Dock door Markers on the map
  const RenderDockDoor = useMemo(() => {
    if (plantConfig && convertedDockDoorsData && enableDockDoor) {
      let { dockDoors } = plantConfig.indoorMap;
      if (!dockDoors.isEnabled) return;

      return convertedDockDoorsData.map((dockMarker, index) => {
        let height = dockDoors.marker.size[1];
        let width = dockDoors.marker.size[0];
        let markerData = dockMarker;
        let isHorizontal = markerData[0]?.angle === 0;
        if (isHorizontal) {
          let temp = height;
          height = width;
          width = temp;
        }
        const dockPosition = dockDoors.position;

        let reactIcon = convertReactComponentToHtmlIcon(
          <DockIcon
            index={index}
            plantConfig={plantConfig}
            dockData={markerData}
          />,
          `dock-door ${getDockPosition(dockPosition, isHorizontal)} ${
            markerData[0].readyForLoading == 1
              ? "ready-to-load"
              : "not-ready-to-load"
          } ${isHorizontal ? "dock-door-with-90-angle" : ""}`,
          [width, height],
          [0, 55]
        );

        let coordinateCorrection = {
          x: dockDoors?.eachItemOffset?.[markerData[0].dock]?.x || 0,
          y: dockDoors?.eachItemOffset?.[markerData[0].dock]?.y || 0
        };

        return (
          <Marker
            key={index}
            type="dock"
            data={markerData}
            eventHandlers={{
              click: (e) => {
                setSelectedMarkerData(markerData);
                setSelectedMarkerType("dock");
                setIsMarkerPopupOpen(true);
              }
            }}
            position={[
              markerData[0].currentPos.lat + coordinateCorrection.y,
              markerData[0].currentPos.lng + coordinateCorrection.x
            ]}
            icon={reactIcon}
          />
        );
      });
    }
  }, [convertedDockDoorsData, plantConfig, enableDockDoor]);

  //Render Production line Markers on the map
  const RenderProductionLine = useMemo(() => {
    if (plantConfig && convertedProdLineData && enableProductionLine) {
      //Get Plant Config data
      let { productionLines } = plantConfig.indoorMap;
      if (!productionLines.isEnabled) return null;

      return convertedProdLineData?.map((productionLine, index) => {
        //Get Correction values
        let correctionUnit = productionLines.eachItemOffset[
          productionLine.LINE_NAME
        ]
          ? productionLines.eachItemOffset[productionLine.LINE_NAME]
          : { x: 0, y: 0 };
        //Popup Data
        let productionData = productionLine;
        //Convert Lat and Lng to X and Y
        let coordinateCorrection = getLocationGroupOffset(
          productionData.currentPos,
          correctionUnit
        );
        //Marker Icon
        let reactIcon = convertReactComponentToHtmlIcon(
          <ProdLineCard lineData={productionData} index={index} />
        );
        return (
          <Marker
            type="productionLine"
            data={productionData}
            eventHandlers={{
              click: (e) => {
                setSelectedMarkerData(productionData);
                setSelectedMarkerType("productionLine");
                setIsMarkerPopupOpen(true);
              }
            }}
            key={index}
            position={coordinateCorrection}
            icon={reactIcon}
          ></Marker>
        );
      });
    }
  }, [convertedProdLineData, plantConfig, enableProductionLine]);

  //Render battery station Markers on the map
  const RenderBatteryStation = useMemo(() => {
    if (plantConfig && convertedBattStationData && enableBatteryStationObj) {
      let { batteryStations } = plantConfig.indoorMap;
      if (!batteryStations.isEnabled) return;
      return convertedBattStationData.map((batteryStation, index) => {
        if (batteryStation.stationName == openedBatteryStationName) {
          setSelectedMarkerData(batteryStation);
        }

        let coordinateCorrection = {
          x:
            batteryStations?.eachItemOffset?.[batteryStation.stationName]?.x ||
            0,
          y:
            batteryStations?.eachItemOffset?.[batteryStation.stationName]?.y ||
            0
        };
        let batteryStationData = batteryStation;
        let reactIcon = convertReactComponentToHtmlIcon(
          <BatteryStationObject
            stationData={batteryStationData}
            index={index}
          />,
          "battery-station-marker"
        );
        return (
          <Marker
            type="batteryStationObj"
            data={batteryStationData}
            eventHandlers={{
              click: (e) => {
                setSelectedMarkerData(batteryStationData);
                setSelectedMarkerType("batteryStationObj");
                setIsMarkerPopupOpen(true);
              }
            }}
            key={index}
            riseOnHover
            position={[
              batteryStationData.currentPos.lat + coordinateCorrection.x,
              batteryStationData.currentPos.lng + coordinateCorrection.y
            ]}
            icon={reactIcon}
          >
            <Tooltip direction="top">{batteryStationData.stationName}</Tooltip>
          </Marker>
        );
      });
    }
  }, [convertedBattStationData, plantConfig, enableBatteryStationObj]);

  //Render Map Object Instance with the Map Image and Bounds
  const RenderMap = useMemo(() => {
    if (plantConfig && appConfig && mapObject && plantCode) {
      let img = getPlantMapImageUrl(appConfig, plantCode, "WAREHOUSE_MAP");
      //Get Plant Config
      const { mapBg } = plantConfig.indoorMap;
      if (img && mapBg) {
        //Get Image, Image Bounds (limit), X1,Y1 , X2,Y2 coordinates of image and Image bg color
        const { bounds, coordinates } = mapBg;
        //(X1,Y1) and (X2,Y2) data of map coordinates
        const { startX, startY, endX, endY } = coordinates;

        //Downscale start coordinate
        let startConvertedCoordinates = mapCoordinate(
          startX,
          startY,
          "map",
          bounds
        );
        let startConvertedPoints = L.point(
          startConvertedCoordinates.x,
          startConvertedCoordinates.y
        );
        let startConvertedLatLng =
          mapObject.map.target.layerPointToLatLng(startConvertedPoints);

        //Downscale end coordinate
        let endConvertedCoordinates = mapCoordinate(endX, endY, "map", bounds);
        let endConvertedPoints = L.point(
          endConvertedCoordinates.x,
          endConvertedCoordinates.y
        );
        let endConvertedLatLng =
          mapObject.map.target.layerPointToLatLng(endConvertedPoints);

        //Final Downscaled bounds for Image
        let overlayBounds = [
          [startConvertedLatLng.lat, startConvertedLatLng.lng],
          [endConvertedLatLng.lat, endConvertedLatLng.lng]
        ];
        return (
          <ImageOverlay
            fetchPriority="high"
            url={img}
            bounds={overlayBounds}
            eventHandlers={{
              load: () => {
                setMapLoader(false);
              }
            }}
          />
        );
      }
    }
  }, [mapObject, plantConfig, appConfig, plantCode]);

  //Render Destination Pointers Markers on the map
  const RenderDestinationPointers = useMemo(() => {
    if (plantConfig && enableDestinationPointers && convertedSocketData) {
      const {
        destinationPointers: {
          isEnabled,
          coordinateCorrection,
          destinationCorrection
        }
      } = plantConfig.indoorMap;

      if (!isEnabled) return;

      return convertedSocketData
        .filter(({ destination, source }) => !!destination && !!source)
        .map(
          (
            {
              currentPos,
              destinationCurrentPos,
              destination,
              vehicleNumber,
              sourceCurrentPos,
              status,
              source,
              sku,
              lpn_1,
              lpn_2
            },
            index
          ) => {
            let locationPointingTo = destination;

            // NEW LOGIC HERE
            //points towards source instead of destionation
            const shouldPointToDestination =
              status === "ActiveFull" ||
              (sku != null && (lpn_1 != null || lpn_2 != null));

            if (!shouldPointToDestination && source) {
              destinationCurrentPos = { ...sourceCurrentPos };
              locationPointingTo = source;
            }

            let sourceAndDestination;

            if (usesNewPointerLogic(plantConfig.plantCode)) {
              sourceAndDestination = getDestinationPointerOffsetV2(
                { currentPos, destinationCurrentPos },
                coordinateCorrection,
                destinationCorrection,
                locationPointingTo
              );
            } else {
              let offsetObject;

              if (destinationCorrection[destination]) {
                offsetObject = {
                  coordinateCorrection,
                  value: destinationCorrection[destination]
                };
              } else {
                let otherDestinationOffset = destinationCorrection.OTHERS;
                offsetObject = {
                  coordinateCorrection,
                  value: {
                    x: otherDestinationOffset.x,
                    y: otherDestinationOffset.y
                  }
                };
              }

              sourceAndDestination = getDestinationPointerOffset(
                { currentPos, destinationCurrentPos },
                offsetObject
              );
            }

            if (sourceAndDestination) {
              return (
                <DestinationPointer
                  key={index}
                  index={index}
                  vehicleNumber={vehicleNumber}
                  sourceAndDestination={sourceAndDestination}
                />
              );
            } else {
              console.error(
                "Something wrong with this LGV's location and destination",
                { currentPos, destinationCurrentPos }
              );
              return null;
            }
          }
        );
    }
  }, [convertedSocketData, plantConfig, enableDestinationPointers]);

  /**
   * Group the Raw Block Storage data into below sections
   * 1.Location Name
   * 2.Pallet Position
   * 3.Row Level
   * @param {} blockData
   * @returns locationGroup {}
   */
  const formatBlockDataStructure = (blockData, type) => {
    /**
     * Group all the locations with key as location name
     */
    let locationGroup = {};
    blockData.forEach((location) => {
      let name;
      if (type === "rack") {
        name = formatName(location.location);
      } else {
        name = location.location;
      }
      if (!locationGroup[name]) {
        locationGroup[name] = [];
      }
      locationGroup[name].push(location);
    });

    if (type === "block") {
      /**
       * Group all the pallets inside location with key as palletPosition as key
       */
      for (let location in locationGroup) {
        let palletGroup = {};
        let currentLocationIterated = locationGroup[location];
        currentLocationIterated.forEach((palletSet) => {
          let pallet = palletSet.palletPosition;
          if (!palletGroup[pallet]) {
            palletGroup[pallet] = [];
          }
          palletGroup[pallet].push(palletSet);
        });
        locationGroup[location] = palletGroup;

        /**
         * Group all the Levels inside Pallets with key as Pallet Level
         */
        for (let pallet in palletGroup) {
          let levelGroup = {};
          palletGroup[pallet].forEach((levelSet) => {
            let level = levelSet.row;
            if (!levelGroup[level]) {
              levelGroup[level] = [];
            }
            levelGroup[level].push(levelSet);
          });
          palletGroup[pallet] = levelGroup;
        }
      }
    }
    return locationGroup;
  };

  const blockClickHandler = (blockData, type) => {
    setIsBlockRackModalOpen(true);
    dispatch(setSelectedBlockRackLocationData(blockData));
    setIsRackModal(type === "rack");
  };

  /**
   * Generate Block Coordinates, Status and Pallets
   * Combine the Status and Pallets to the list of data from DB data
   * Conditions to render the location boxes
   * 1. Total Number of position - totalPositions
   * 2. When to change the direction - positionTransition
   * 3. In Which direction to start - locationOrientation
   * 4. In which direction to render after change of direction - positionOrientation
   */

  /**
   * Convert the grouped locations data into different list for rendering it as list in the marker
   * Split the list according to the length of the locationsList
   * @param {*} transitionPallet-number locationsList-object
   * @returns sectionList-array
   */

  /**
   * Generating Location Marker with the list data
   * Create Sections with the markerSectionGenerator function
   * Columns of the marker = length of the list
   * Each List is a row of pallets
   * If length of the sectionsData = 1, the markers are single stack storage
   * else if sectionsData > 1, the markers are double stack storage
   * @param {*} locationMetaData-Object locationsList-Object
   * @returns HTML
   */

  /**
   * Returing Meta data of a single location
   * @param {*} location-object
   * @returns metaData-object
   */
  const getMetaDataOfLocation = (location, type) => {
    if (type === "block") {
      return {
        latitude: location.latitude,
        longitude: location.longitude,
        location: location.location,
        totalPositions: location.totalPositions,
        positionTransition: location.positionTransition,
        positionOrientation: location.positionOrientation,
        locationOrientation: location.locationOrientation,
        positionLength: location.positionLength
      };
    } else {
      return {
        latitude: location.latitude,
        longitude: location.longitude,
        location: location.location,
        totalPositions: location.totalPositions,
        locationOrientation: location.locationOrientation,
        positionLength: location.positionLength
      };
    }
  };

  /**
   * Create Markers according to the List of locations available
   * Algo:
   * 1.Iterate raw data
   * 2.Get Meta data of the location
   * 3.Generate Group of locations
   * 4.Create Marker icons with location data
   * 5.Convert the coordinates
   * 6.Plot the Markers with the coordinates and icon generated
   * @returns Markers-reactleaflet
   */

  useEffect(() => {
    if (!plantConfig || !auth?.accessToken || !mapObject) return;

    let blockTimeoutId;
    let rackTimeoutId;
    let isMounted = true;

    const pollData = async ({
      url, //endpoint
      setStates = [], //array for multiple setters to updated
      dispatchActions = [], //array of multiple actions dispatch
      setTimeoutFn //polling frequency
    }) => {
      const data = await fetchDataWithRetry(url);

      if (isMounted && data && data !== DB_ERROR) {
        // Update all local states
        setStates.forEach((setter) => setter(data));

        // Dispatch  actions (with or without normalization)
        dispatchActions.forEach(({ action, normalizerKey }) => {
          const payload = normalizerKey
            ? normalizeData(data, normalizerKey)
            : data;
          dispatch(action(payload));
        });
      }

      setTimeoutFn(() => {
        pollData({ url, setStates, dispatchActions, setTimeoutFn });
      }, BLOCK_RACK_POLLING_INTERVAL);
    };

    rackTimeoutId = pollData({
      url: `${rackStorage}${plantConfig.plantCode}`,
      setStates: [],
      dispatchActions: [
        { action: setRackStorageData },
        { action: setRacksData, normalizerKey: "rack" }
      ],
      setTimeoutFn: (fn) =>
        (rackTimeoutId = setTimeout(fn, BLOCK_RACK_POLLING_INTERVAL))
    });

    blockTimeoutId = pollData({
      url: `${blockStorage}${plantConfig.plantCode}`,
      setStates: [],
      dispatchActions: [
        { action: setBlocksData, normalizerKey: "block" },
        { action: setBlockStorageData }
      ],
      setTimeoutFn: (fn) =>
        (blockTimeoutId = setTimeout(fn, BLOCK_RACK_POLLING_INTERVAL))
    });

    return () => {
      isMounted = false;
      clearTimeout(blockTimeoutId);
      clearTimeout(rackTimeoutId);
    };
  }, [plantConfig, mapObject, auth?.accessToken]);

  const isBlockObjectValid = (obj) => {
    if (!obj) return null;

    const firstLocationPalletIndex = Object.keys(obj)[0];
    const firstObjectPalletValue = obj[firstLocationPalletIndex];

    if (!firstObjectPalletValue) return null;

    const firstPalletLevelIndex = Object.keys(firstObjectPalletValue)[0];
    const firstPalletLevelValue =
      firstObjectPalletValue[firstPalletLevelIndex]?.[0];

    return firstPalletLevelValue ? firstPalletLevelValue : null;
  };

  useEffect(() => {
    if (
      plantConfig &&
      isBlockRackNonLgvEnabled(plantCode) &&
      mapObject &&
      Object.keys(blockStorageData).length !== 0
    ) {
      let obj = countBlocksUsingLPNs(blockStorageData);
      dispatch(setBlocksCount(obj));
    }
  }, [blockStorageData]);

  const RenderBlockStorage = useMemo(() => {
    if (
      plantConfig &&
      isBlockRackNonLgvEnabled(plantCode) &&
      mapObject &&
      Object.keys(blocksCount).length &&
      Object.keys(blockStorageData).length !== 0
    ) {
      let { blockStorage } = plantConfig.indoorMap;
      if (!blockStorage.isEnabled) return;
      const formattedBlockData = formatBlockDataStructure(
        blockStorageData,
        "block"
      );
      return Object.values(formattedBlockData).map((block, index) => {
        const blockObject = Object.values(block);
        let validateObject = isBlockObjectValid(blockObject);
        if (validateObject) {
          //Metadata of the Location
          let metaData = validateObject;
          let {
            latitude,
            longitude,
            location,
            locationOrientation,
            positionLength
          } = getMetaDataOfLocation(metaData, "block");

          //Convert Start
          //Get Block Details from Config
          const { blockStorage } = plantConfig.indoorMap;
          const { marker, verticalMarker } = blockStorage;

          //New Algo ------------------------------------- Start

          //East or North Location Blocks

          const convertedPositionObj = blockStorageCoordinateConverter({
            latitude,
            longitude,
            location,
            locationOrientation,
            positionLength,
            blockStorageConfig: blockStorage,
            mapObject
          });

          //New Algo ------------------------------------- End

          //Destructure Overall Blocks Height and Width and Single stack height and width
          const blockSizeConfig = {
            blockWidth: marker.size.width,
            blockHeight: marker.size.height,
            singleStackWidth: marker.singleStack.size.width,
            singleStackHeight: marker.singleStack.size.height
          };
          //Set Dynamic Width and Height of Block from Config
          //Set the Dynamic Width and Height of Single Stack (PBC row) pallet from Config

          setBlockStorageSize(blockSizeConfig);

          if (verticalMarker) {
            const verticalBlockSizeConfig = {
              blockWidth: verticalMarker.size.width,
              blockHeight: verticalMarker.size.height,
              singleStackWidth: verticalMarker.singleStack.size.width,
              singleStackHeight: verticalMarker.singleStack.size.height
            };

            setVerticalBlockStorageSize(verticalBlockSizeConfig);
          }
          //Generate Block Icon with Data
          const reactIcon = convertReactComponentToHtmlIcon(
            <GenerateHTMLBlockIcon
              locationMetaData={metaData}
              locationsList={block}
              type="block"
              blocksCount={blocksCount}
              marker={marker}
              isExceptional={verticalMarker?.isExceptional}
            />
          );

          //Return and Render the Marker
          if (!convertedPositionObj) return null;
          return (
            <BlockMarker
              key={index}
              location={location}
              convertedPositionObj={convertedPositionObj}
              metaData={metaData}
              block={block}
              blockClickHandler={blockClickHandler}
              reactIcon={reactIcon}
              rotateMarker={rotateMarker}
              setPopUpDetails={setPopUpDetails}
              markerRefs={markerRefs}
              isBlockRackModalOpen={isBlockRackModalOpen}
              hoveredLocation={hoveredLocation}
              setHoveredLocation={setHoveredLocation}
            />
          );
        }
      });
    }
  }, [mapObject, blocksCount, plantConfig, filterResults, selectedResult]);

  useEffect(() => {
    if (
      plantConfig &&
      isBlockRackNonLgvEnabled(plantCode) &&
      mapObject &&
      Object.keys(rackStorageData).length !== 0
    ) {
      let obj = countRacksUsingLPNs(rackStorageData);
      dispatch(setRacksCount(obj));
    }
  }, [rackStorageData]);

  const RenderRackStorage = useMemo(() => {
    if (
      plantConfig &&
      isBlockRackNonLgvEnabled(plantCode) &&
      mapObject &&
      Object.keys(racksCount).length &&
      rackStorageData.length !== 0
    ) {
      let { rackStorage } = plantConfig.indoorMap;
      if (!rackStorage.isEnabled) return;

      let rackData = [...rackStorageData];
      let formattedRackData = formatBlockDataStructure(rackData, "rack");
      return Object.values(formattedRackData).map((rack, index) => {
        let metaData = Object.values(rack)[0];
        let { latitude, longitude, location } = getMetaDataOfLocation(
          metaData,
          "rack"
        );
        //Convert Start

        // //Get Rack Details from Config
        // const { rackStorage } = plantConfig.indoorMap;
        const { marker } = rackStorage;
        const convertedPositionObj = rackStorageCoordinateConverter({
          latitude,
          longitude,
          location,
          rackStorageConfig: rackStorage,
          mapObject
        });

        //Destructure Overall Blocks Height and Width and Single stack height and width
        const blockSizeConfig = {
          rackWidth: marker.size.width,
          rackHeight: marker.size.height
        };
        //Set Dynamic Width and Height of Rack from Config
        setRackStorageSize(blockSizeConfig);

        //Generate Rack Icon
        let reactIcon = convertReactComponentToHtmlIcon(
          <GenerateHTMLBlockIcon
            locationMetaData={metaData}
            locationsList={rack}
            type="rack"
            racksCount={racksCount}
          />,
          "pallets-wrapper"
        );
        //Render Rack Marker
        if (!convertedPositionObj) return null;
        return (
          <Marker
            key={index}
            eventHandlers={{
              click: () => blockClickHandler(rack, "rack")
            }}
            position={convertedPositionObj}
            icon={reactIcon}
          >
            <Tooltip
              sticky
              key={location}
              direction="top"
              permanent={false}
              interactive={false}
              autoPan={false}
              autoClose={false}
            >
              {formatName(location)}
            </Tooltip>
            <Popup>{formatName(location)}</Popup>
          </Marker>
        );
      });
    }
  }, [mapObject, rackStorageData, racksCount, filterResults, selectedResult]);

  /**
   * Generate the React leaflet Icon with the specified coordinates for Non-lgv inventory icon
   * On click of the icon, Non-LGV Inventory Modal opens up
   */
  const RenderNonLgvStorage = useMemo(() => {
    if (
      plantConfig &&
      nonLgvData &&
      isBlockRackNonLgvEnabled(plantCode) &&
      mapObject
    ) {
      //Get Plant Config and Non LGV Settings
      const {
        nonLgvStorage: { marker, coordinate, isEnabled }
      } = plantConfig.indoorMap;

      if (!isEnabled) return;

      const shouldHiglight = () => {
        if (selectedResult) {
          const hasNonLGVItem = selectedResult.storageType === "nonLGV";
          return hasNonLGVItem ? "non-lgv-highlighter" : "";
        }

        return "";
      };

      //Create Marker Icon
      const reactIcon = convertReactComponentToHtmlIcon(
        <LgvIcon
          data="NON-LGV INVENTORY"
          status="white"
          className="non-lgv-icon-style"
          nonLGVRotation={marker.rotation}
        />,
        `non-lgv-icon ${shouldHiglight()}`,
        marker.size,
        [0, 0]
      );

      const convertedPositionObj = nonLGVCoordinateConverter(
        plantConfig.indoorMap.nonLgvStorage,
        mapObject
      );

      //Generate and return Non LGV Button
      return (
        <Marker
          key="non-lgv"
          position={convertedPositionObj}
          icon={reactIcon}
          eventHandlers={{
            click: () => dispatch(setIsNonLgvModalOpen(true))
          }}
        ></Marker>
      );
    }
  }, [nonLgvData, mapObject, plantConfig, filterResults, selectedResult]);

  const MapAligner = ({ onReady }) => {
    const map = useMap();

    useEffect(() => {
      const handleMoveEnd = () => {
        onReady();
      };

      map.once("moveend", handleMoveEnd);
      return () => {
        map.off("moveend", handleMoveEnd);
      };
    }, [map, onReady]);

    return null;
  };

  return (
    <div className="map-container-wrapper">
      <div
        draggable
        onMouseDown={(event) => onDragStart(event)}
        onMouseUp={(event) => onDragEnd(event)}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        id="map-container-id"
        className="map-container"
      >
        {!mapReady || mapLoader || !connectionStatus ? (
          <Loader connectionStatus={connectionStatus} />
        ) : null}
        <MapContainer
          disableDoubleClickZoom
          id="indoor-map-id-reference"
          style={{
            height: "100%",
            width: "100%",
            borderRadius: "10px"
          }}
          center={[0, 0]}
          zoom={0}
          ref={mapRef}
          maxBoundsViscosity={1.0}
          crs={L.CRS.Simple}
          attributionControl={false}
          scrollWheelZoom={false}
          whenReady={(map) => setMapObject({ map })}
          zoomControl={false}
        >
          {RenderMap}
          {RenderBlockStorage}
          {RenderRackStorage}
          {RenderDockDoor}
          {RenderLgv}
          {RenderProductionLine}
          {RenderBatteryStation}
          {RenderNonLgvStorage}
          {RenderDestinationPointers}
          <CustomPopup popUpDetails={popUpDetails} />
          <ReValidateMap zoomLevel={zoomLevel} />
          <MapAligner onReady={() => setMapReady(true)} />
          <PanToLocation
            plantConfig={plantConfig}
            mapObject={mapObject}
            setZoomLevel={setZoomLevel}
            liveLgvData={convertedSocketData}
          />
        </MapContainer>
      </div>
      <ZoomControls
        mapObject={mapObject}
        zoomLevel={zoomLevel}
        setZoomLevel={setZoomLevel}
        currentWheelZoom={currentWheelZoom}
        setCurrentWheelZoom={setCurrentWheelZoom}
        centerMapView={centerMapView}
        maxZoom={maxZoom}
      />
      <BillboardBanner socket={socket} />
      {plantConfig && <LegendsButton />}
      {plantConfig && <FilterButton />}
      <TrailerArrivalNotification socket={socket} />

      <MinimizedWebObjects />

      <FollowingLgvObjects />
    </div>
  );
};

export default IndoorMap;
